import React, { useEffect } from 'react';
import { X, Download, ZoomIn, ZoomOut } from 'lucide-react';
import { BASE_URL } from '../api/client';

interface DocumentData {
  document_id: number;
  document_type: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  is_verified: boolean;
  uploaded_at: string;
}

interface DocumentLightboxProps {
  document: DocumentData;
  onClose: () => void;
}

export const DocumentLightbox: React.FC<DocumentLightboxProps> = ({ document: doc, onClose }) => {
  const [zoom, setZoom] = React.useState(1);

  useEffect(() => {
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';

    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `${BASE_URL}/${doc.file_path}`;
    link.download = doc.file_name;
    link.click();
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const isImage = doc.mime_type?.startsWith('image/');
  const isPdf = doc.mime_type === 'application/pdf';

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="lightbox-header">
          <div className="lightbox-title">
            <h3>{doc.document_type.replace(/_/g, ' ')}</h3>
            <p className="lightbox-filename">{doc.file_name}</p>
          </div>
          <div className="lightbox-actions">
            {isImage && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="lightbox-btn"
                  disabled={zoom <= 0.5}
                  title="Zoom out"
                >
                  <ZoomOut size={20} />
                </button>
                <span className="zoom-level">{Math.round(zoom * 100)}%</span>
                <button
                  onClick={handleZoomIn}
                  className="lightbox-btn"
                  disabled={zoom >= 3}
                  title="Zoom in"
                >
                  <ZoomIn size={20} />
                </button>
              </>
            )}
            <button onClick={handleDownload} className="lightbox-btn" title="Download">
              <Download size={20} />
            </button>
            <button onClick={onClose} className="lightbox-btn" title="Close">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="lightbox-content">
          {isImage ? (
            <div className="lightbox-image-container">
              <img
                src={`${BASE_URL}/${doc.file_path}`}
                alt={doc.document_type}
                style={{ transform: `scale(${zoom})` }}
                className="lightbox-image"
              />
            </div>
          ) : isPdf ? (
            <iframe
              src={`${BASE_URL}/${doc.file_path}`}
              title={doc.file_name}
              className="lightbox-pdf"
            />
          ) : (
            <div className="lightbox-unsupported">
              <p>Preview not available for this file type</p>
              <button onClick={handleDownload} className="btn btn-primary">
                <Download size={20} />
                Download File
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="lightbox-footer">
          <div className="lightbox-info">
            <span className="info-item">
              <strong>Size:</strong> {(doc.file_size / 1024).toFixed(2)} KB
            </span>
            <span className="info-item">
              <strong>Uploaded:</strong>{' '}
              {new Date(doc.uploaded_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
            <span className="info-item">
              <strong>Status:</strong>{' '}
              <span className={`status-badge ${doc.is_verified ? 'verified' : 'pending'}`}>
                {doc.is_verified ? 'Verified' : 'Pending'}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
